import {
  BlockFragment,
  Campaign,
  UpsertCampaignMutationVariables,
} from '__generated__/queries-newsletter';
import {
  CreateBlocks,
  CreateSections,
} from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/emptyTypes';
import { compose, differenceWith, map, omit } from 'ramda';
import { v4 as uuid } from 'uuid';
import { BlockItemType } from '../ContentPage/Items/utils';
import { checkDate } from '../utils';

const getCampaignInfo = (item: Record<string, any>) => ({
  subject: item.subject,
  preHeader: item.preHeader,
  utmContent: item.utmContent,
  availableAt: item.availableAt,
  rawMedias: item.rawMedias,
  newsletter: {
    connect: {
      id: item.newsletter.id,
    },
  },
});

const mapCreateBlocks = (block: CreateBlocks) => ({
  ...omit(['__typename', 'updatedAt', 'createdAt', 'isCreate', 'media'], block),
  id: uuid(),
});

const updateBlocks = (block: CreateBlocks) => ({
  data: {
    ...omit(
      ['__typename', 'updatedAt', 'createdAt', 'id', 'isCreate', 'media'],
      block,
    ),
  },
  where: {
    id: block.id,
  },
});

const mapSections = (section: CreateSections) => ({
  ...omit(
    ['__typename', 'updatedAt', 'createdAt', 'media', 'isCreate'],
    section,
  ),
  blocks: {
    create: section.blocks
      .filter((block: any) => block.isCreate)
      .map(mapCreateBlocks as any),
    ...(!section.isCreate
      ? {
          update: section.blocks
            .filter((block: any) => !block.isCreate)
            .map(updateBlocks as any),
        }
      : {}),
  },
});

const updateSections = (
  section: CreateSections,
  prevSection: CreateSections,
) => ({
  data: {
    ...omit(
      ['__typename', 'updatedAt', 'createdAt', 'media', 'id', 'isCreate'],
      section,
    ),
    blocks: {
      create: section.blocks
        .filter(
          (block) => !prevSection.blocks.find(({ id }) => id === block.id),
        )
        .map(mapCreateBlocks as any),
      update: section.blocks
        .filter((block) => prevSection.blocks.find(({ id }) => id === block.id))
        .map(updateBlocks as any),
      delete: prevSection
        ? compose(
            map((x: BlockFragment) => ({ id: x.id })),
            differenceWith(
              (x: BlockFragment, y: BlockFragment) => x.id === y.id,
            ),
          )(prevSection.blocks, section.blocks)
        : [],
    },
  },
  where: {
    id: section.id,
  },
});

export const campaignMutationVariables = (
  currentItem: Record<string, any>,
  oldItem: Record<string, any>,
  campaignId: string,
  isCreate: boolean,
): UpsertCampaignMutationVariables => {
  let variables: UpsertCampaignMutationVariables;
  if (isCreate) {
    const createId = uuid();
    variables = {
      create: {
        id: createId,
        ...getCampaignInfo(currentItem),
        sections: {
          create: currentItem.sections
            .filter((section: CreateSections) => section.isCreate)
            .map(mapSections),
        },
      },
      update: {
        ...getCampaignInfo(currentItem),
        sections: {
          create: currentItem.sections.map(mapSections),
        },
      },
      where: {
        id: createId,
      },
    };
  } else {
    variables = {
      create: {
        id: campaignId,
        ...getCampaignInfo(currentItem),
        sections: {
          create: currentItem.sections
            .filter((section: CreateSections) => section.isCreate)
            .map(mapSections),
        },
      },
      update: {
        ...getCampaignInfo(currentItem),
        sections: {
          create: currentItem.sections
            .filter((section: CreateSections) => section.isCreate)
            .map(mapSections),
          update: currentItem.sections
            .filter((section: CreateSections) => !section.isCreate)
            .map((section: CreateSections) =>
              updateSections(
                section,
                oldItem.sections.find(
                  (oldSection: CreateSections) => section.id === oldSection.id,
                ),
              ),
            ),
          delete: oldItem.sections.length
            ? compose(
                map((x: CreateSections) => ({ id: x.id })),
                differenceWith(
                  (x: CreateSections, y: CreateSections) => x.id === y.id,
                ),
              )(oldItem.sections, currentItem.sections)
            : [],
        },
      },
      where: {
        id: campaignId,
      },
    };
  }
  return variables;
};

export const isCampaignValid = (campaignItem: Campaign) => {
  let isblockValid = true;
  campaignItem.sections.forEach((item) => {
    item.blocks.forEach((blockItem: BlockItemType) => {
      if (!validateBlock(blockItem)) {
        isblockValid = false;
        //eslint-disable-next-line
        return;
      }
    });
  });
  if (!isblockValid) {
    return {
      validOrError: false,
      errorReason:
        'Un ou plusieurs blocks de la newsletter contiennent des erreurs',
    };
  }

  if (!campaignItem.subject) {
    return {
      validOrError: false,
      errorReason: 'Le sujet est obligatoire',
    };
  }
  if (!campaignItem.newsletter) {
    return {
      validOrError: false,
      errorReason: 'La newsletter est obligatoire',
    };
  }
  if (!campaignItem.availableAt) {
    return {
      validOrError: false,
      errorReason: "La date d'envoie est obligatoire",
    };
  }

  const linkEmpty = campaignItem.sections.some((section) =>
    section.blocks.some((block) => block.url === ''),
  );
  if (linkEmpty) {
    return {
      validOrError: false,
      errorReason: 'Vous devez saisir un lien dans tous les blocks',
    };
  }

  const campaignSendDate = new Date(campaignItem.availableAt);
  const isPast = checkDate(campaignSendDate);

  if (isPast) {
    return {
      validOrError: false,
      errorReason:
        'Les dates antérieures à la date du jour ne sont pas possibles',
    };
  }

  return { validOrError: true, errorReason: null };
};

const validateBlock = (blockItem: BlockItemType) => {
  let isValid = true;
  Object.keys(blockItem).forEach((key) => {
    if (key.includes('InError')) {
      if (blockItem[key as keyof BlockItemType] === true) {
        isValid = false;
        //eslint-disable-next-line
        return;
      }
    }
  });

  return isValid;
};
